import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { InfoTip } from "@/components/InfoTip";
import {
  ShieldCheck, Check, Circle, ArrowRight, Vault, Camera, AlertTriangle,
  FileText, Eye, PenLine, CalendarClock, Crown, Scale, Gavel, XCircle, CheckCircle2,
} from "lucide-react";

const BAND = {
  Comprehensive: { bar: "bg-[#177245]", text: "text-[#177245]", ring: "border-[#A7F3D0]", soft: "bg-[#ECFDF5]" },
  Substantial: { bar: "bg-brand", text: "text-brand", ring: "border-brand/30", soft: "bg-brand/5" },
  Partial: { bar: "bg-amber", text: "text-amber-ink", ring: "border-amber-border", soft: "bg-amber-soft" },
  Limited: { bar: "bg-[#B91C1C]", text: "text-[#B91C1C]", ring: "border-[#FECACA]", soft: "bg-[#FEF2F2]" },
};
const NEUTRAL = { bar: "bg-[#CBD5E1]", text: "text-[#64748B]", ring: "border-[#E2E8F0]", soft: "bg-[#F1F5F9]" };

function Card({ children, testId, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      data-testid={testId}
      className={`rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-6 ${className}`}
    >
      {children}
    </motion.div>
  );
}

function StrengthCard({ strength }) {
  const notStarted = strength.percent === 0;
  const b = notStarted ? NEUTRAL : (BAND[strength.band] || BAND.Partial);
  const label = notStarted ? "Not started" : strength.band;
  const explanation = notStarted
    ? "Add your first room photos to begin building documentation thoroughness."
    : strength.explanation;
  return (
    <Card testId="evidence-strength-card" className={`border-2 ${b.ring}`}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <ShieldCheck size={20} className={b.text} />
          <h2 className="font-heading text-xl font-semibold text-navy">Documentation Thoroughness</h2>
          <InfoTip topic="evidenceStrength" />
        </div>
        <span data-testid="strength-band" className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold border ${b.ring} ${b.soft} ${b.text}`}>
          {label}
        </span>
      </div>
      <div className="mt-4 flex items-end gap-3">
        <span data-testid="strength-percent" className={`font-heading text-4xl font-bold ${b.text}`}>{strength.percent}%</span>
        <span className="mb-1 text-sm text-[#64748B]">documentation thoroughness</span>
      </div>
      <div className="mt-3 h-2.5 w-full rounded-full bg-[#F1F5F9] overflow-hidden">
        <motion.div
          initial={{ width: 0 }} animate={{ width: `${strength.percent}%` }} transition={{ duration: 0.7, ease: "easeOut" }}
          className={`h-full rounded-full ${b.bar}`}
        />
      </div>
      <p className="mt-3 text-sm text-[#475569] leading-relaxed">{explanation}</p>
      <p className="mt-2 text-[11px] text-[#94A3B8]">Reflects how thoroughly your rental is documented. Not legal advice or a guarantee of outcome.</p>
    </Card>
  );
}

function PlanCard({ plan }) {
  const navigate = useNavigate();
  const done = plan.filter((s) => s.done).length;
  const next = plan.find((s) => !s.done);
  return (
    <Card testId="protection-plan-card">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <CheckCircle2 size={20} className="text-brand" />
          <h2 className="font-heading text-xl font-semibold text-navy">Deposit Protection Plan</h2>
          <InfoTip topic="protectionPlan" />
        </div>
        <span className="text-sm font-semibold text-[#94A3B8]" data-testid="plan-progress">{done}/{plan.length}</span>
      </div>
      <p className="mt-1 text-sm text-[#64748B]">Your guided steps to organize strong, well-documented evidence.</p>
      <div className="mt-4 space-y-2">
        {plan.map((s, i) => (
          <button
            key={s.key}
            data-testid={`plan-step-${s.key}`}
            onClick={() => navigate(s.cta_route)}
            className={`w-full flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${
              s.done ? "border-[#E2E8F0] bg-[#F8FAFC]" : "border-[#E2E8F0] hover:border-brand hover:bg-brand/5"
            }`}
          >
            <span className={`grid place-items-center h-6 w-6 rounded-full shrink-0 ${
              s.done ? "bg-[#177245] text-white" : (next && next.key === s.key ? "bg-brand text-white" : "bg-[#F1F5F9] text-[#94A3B8]")
            }`}>
              {s.done ? <Check size={14} strokeWidth={3} /> : <Circle size={9} fill="currentColor" />}
            </span>
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-semibold truncate ${s.done ? "text-[#94A3B8] line-through" : "text-navy"}`}>{s.label}</p>
              <p className="text-xs text-[#94A3B8] truncate">{s.hint}</p>
            </div>
            {!s.done && <ArrowRight size={15} className="text-[#CBD5E1] shrink-0" />}
          </button>
        ))}
      </div>
    </Card>
  );
}

function MoneyCard({ money }) {
  const navigate = useNavigate();
  if (!money.has_deposit) {
    return (
      <Card testId="money-protected-card" className="border-brand/30">
        <div className="flex items-center gap-2">
          <Vault size={20} className="text-brand" />
          <h2 className="font-heading text-xl font-semibold text-navy">Money Protected</h2>
        </div>
        <p className="mt-2 text-sm text-[#475569] leading-relaxed">
          Add your deposit amount to see the value your documentation helps protect.
        </p>
        <button
          data-testid="add-deposit-prompt"
          onClick={() => document.querySelector('[data-testid="edit-details-btn"]')?.click()}
          className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline"
        >
          Add deposit amount <ArrowRight size={15} />
        </button>
      </Card>
    );
  }
  const d = money.deadline || {};
  const countdown = d.configured && !d.deposit_returned && typeof d.days_remaining === "number"
    ? (d.days_remaining < 0 ? "Deadline passed" : d.days_remaining === 0 ? "Due today" : `${d.days_remaining} days left`)
    : (d.deposit_returned ? "Deposit returned" : "Not tracked");
  const rows = [
    { icon: Camera, label: "Photos documented", value: money.photos },
    { icon: AlertTriangle, label: "Issues recorded", value: money.issues },
    { icon: FileText, label: "Reports generated", value: money.reports, tip: "evidencePackage" },
    { icon: money.share_signed ? PenLine : Eye, label: "Landlord acknowledgment",
      value: money.share_signed ? "Signed" : (money.share_viewed ? "Viewed" : "Pending"), tip: "landlordAcknowledgment" },
    { icon: CalendarClock, label: "Deposit deadline", value: countdown, tip: "deadlineTracking" },
  ];
  return (
    <Card testId="money-protected-card" className="border-brand/30">
      <div className="flex items-center gap-2">
        <Vault size={20} className="text-brand" />
        <h2 className="font-heading text-xl font-semibold text-navy">Money Protected</h2>
      </div>
      <p className="mt-3 font-heading text-3xl font-bold text-navy" data-testid="deposit-protected-amount">
        ${Number(money.deposit_amount).toLocaleString()}<span className="ml-2 text-sm font-semibold text-[#64748B]">deposit documented</span>
      </p>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center gap-2.5 rounded-xl border border-[#E2E8F0] bg-white px-3.5 py-2.5">
            <r.icon size={16} className="text-brand shrink-0" />
            <span className="text-xs text-[#64748B] flex-1 min-w-0 truncate">{r.label}</span>
            {r.tip && <InfoTip topic={r.tip} />}
            <span className="text-sm font-bold text-navy shrink-0">{r.value}</span>
          </div>
        ))}
      </div>
      {d.configured && !d.deposit_returned && (
        <button onClick={() => navigate(0)} className="mt-3 text-xs text-[#94A3B8] hover:text-brand">
          Documentation helps support communication about your deposit — it does not guarantee any outcome.
        </button>
      )}
    </Card>
  );
}

const PREMIUM_TOOLS = [
  {
    icon: Scale, title: "Dispute preparation", tip: "disputePackage",
    points: ["Deduction auditor", "Dispute letter you review & edit", "PDF appendix of exhibits"],
  },
  {
    icon: Gavel, title: "Escalation Documentation Packet", tip: "smallClaimsPacket",
    points: ["Chronological timeline", "Labeled exhibits", "Chain-of-custody summary", "Demand letter"],
  },
];

function PremiumCard() {
  return (
    <Card testId="premium-value-card" className="border-2 border-amber-border">
      <div className="flex items-center gap-2">
        <Crown size={20} className="text-amber-ink" />
        <h2 className="font-heading text-xl font-semibold text-navy">TenantSeal Plus tools</h2>
        <InfoTip topic="premiumTools" />
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {PREMIUM_TOOLS.map((t) => (
          <div key={t.title} className="rounded-xl border border-[#E2E8F0] bg-white p-4">
            <div className="flex items-center gap-2">
              <t.icon size={17} className="text-brand" />
              <h3 className="font-heading text-base font-bold text-navy">{t.title}</h3>
              <InfoTip topic={t.tip} />
            </div>
            <ul className="mt-2.5 space-y-1.5">
              {t.points.map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm text-[#475569]">
                  <Check size={15} className="text-[#177245] mt-0.5 shrink-0" /> <span>{p}</span>
                  {p.toLowerCase().includes("chain-of-custody") && <InfoTip topic="chainOfCustody" />}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Card>
  );
}

const CAMERA_ROLL = [
  "Random, unlabeled photos",
  "No missing-shot guidance",
  "No timestamps or integrity hashes",
  "No landlord view receipt",
  "No dispute or claims packet",
];
const DD_WAY = [
  "Guided room-by-room coverage",
  "AI condition reports",
  "Chain-of-custody proof",
  "Landlord acknowledgment tracking",
  "Evidence package + dispute letter",
];

function ComparisonCard() {
  return (
    <Card testId="camera-comparison-card">
      <div className="flex items-center gap-2">
        <Camera size={20} className="text-brand" />
        <h2 className="font-heading text-xl font-semibold text-navy">Why not just use your camera?</h2>
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">Camera roll</p>
          <ul className="mt-3 space-y-2">
            {CAMERA_ROLL.map((c) => (
              <li key={c} className="flex items-start gap-2 text-sm text-[#64748B]">
                <XCircle size={15} className="text-[#CBD5E1] mt-0.5 shrink-0" /> {c}
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border-2 border-brand/30 bg-brand/5 p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-brand">TenantSeal</p>
          <ul className="mt-3 space-y-2">
            {DD_WAY.map((c) => (
              <li key={c} className="flex items-start gap-2 text-sm text-navy font-medium">
                <Check size={15} className="text-[#177245] mt-0.5 shrink-0" /> {c}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Card>
  );
}

export function ProtectionLayer({ propertyId, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/properties/${propertyId}/protection`);
      setData(data);
    } catch (e) {
      console.error("protection load failed", e);
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading) {
    return <div className="mt-6 space-y-4"><Skeleton className="h-40 rounded-2xl" /><Skeleton className="h-56 rounded-2xl" /></div>;
  }
  if (!data) return null;

  return (
    <div className="mt-6 space-y-4" data-testid="protection-layer">
      <StrengthCard strength={data.strength} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PlanCard plan={data.plan} />
        <MoneyCard money={data.money} />
      </div>
      <PremiumCard />
      <ComparisonCard />
    </div>
  );
}


// ---- Progressive-disclosure split pieces (used by the property page) ----
function useProtection(propertyId, refreshKey) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/properties/${propertyId}/protection`);
      setData(data);
    } catch (e) {
      console.error("protection load failed", e);
    } finally {
      setLoading(false);
    }
  }, [propertyId]);
  useEffect(() => { load(); }, [load, refreshKey]);
  return { data, loading };
}

// Prominent "Deposit Protection Plan" section (plan steps + money recovery).
export function DepositProtectionPlan({ propertyId, refreshKey }) {
  const { data, loading } = useProtection(propertyId, refreshKey);
  if (loading) return <div className="mt-6"><Skeleton className="h-56 rounded-2xl" /></div>;
  if (!data) return null;
  return (
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="deposit-protection-plan">
      <PlanCard plan={data.plan} />
      <MoneyCard money={data.money} />
    </div>
  );
}

// Documentation Thoroughness card on its own (rendered inside a compact disclosure).
export function EvidenceStrengthCard({ propertyId, refreshKey }) {
  const { data, loading } = useProtection(propertyId, refreshKey);
  if (loading) return <Skeleton className="h-28 rounded-2xl" />;
  if (!data) return null;
  return <StrengthCard strength={data.strength} />;
}

// Below-the-fold value/education: premium tools + camera comparison.
export function PremiumAndComparison() {
  return (
    <div className="mt-6 space-y-4" data-testid="premium-and-comparison">
      <PremiumCard />
      <ComparisonCard />
    </div>
  );
}



const DOT = {
  Comprehensive: "bg-[#177245]", Substantial: "bg-brand", Partial: "bg-amber", Limited: "bg-[#B91C1C]",
};

export function CompactStrengthBadge({ propertyId, testId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get(`/properties/${propertyId}/protection`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [propertyId]);

  if (loading) return <div className="mt-2 h-4 w-40 rounded bg-[#F1F5F9] animate-pulse" data-testid={`${testId}-loading`} />;
  if (!data) return null;

  const { band, percent } = data.strength;
  const notStarted = percent === 0;
  const b = notStarted ? NEUTRAL : (BAND[band] || BAND.Partial);
  const label = notStarted ? "Not started" : band;
  const dot = notStarted ? "bg-[#CBD5E1]" : (DOT[band] || DOT.Partial);
  const next = (data.plan || []).find((s) => !s.done);
  return (
    <div className="mt-2" data-testid={testId}>
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full shrink-0 ${dot}`} />
        <span className={`text-xs font-bold ${b.text}`}>{label}</span>
        <span className="text-xs font-semibold text-[#94A3B8]">{percent}%</span>
        <span className="flex-1 h-1.5 rounded-full bg-[#F1F5F9] overflow-hidden min-w-[40px] max-w-[120px]">
          <span className={`block h-full rounded-full ${b.bar}`} style={{ width: `${percent}%` }} />
        </span>
      </div>
      {!notStarted && band !== "Comprehensive" && next && (
        <p className="mt-1 text-xs text-[#64748B] truncate">Next: {next.label}</p>
      )}
      {notStarted && (
        <p className="mt-1 text-xs text-[#94A3B8] truncate">Add room photos to begin</p>
      )}
    </div>
  );
}
